import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { useUpdateSmartLinkMutation } from '../../../../redux/api/fymAPI';
import { SMLAnalytics } from '../../../analytics/sml/analytics';
import { useEditorSaves } from '../../editor/saves/useEditorSaves';
import { SMLRailPanel } from './SMLRailPanel';
import { SMLThemeSwatch } from './SMLThemeSwatch';
import { useFeaturedThemes } from './useFeaturedThemes';
import styles from './SMLThemePicker.module.scss';

interface SMLThemePickerProps {
  smartLink: SerializedSmartLink;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
}

const allThemes = Object.values(SerializedSmartLinkVersionTheme);

/**
 * Page theme, picked straight from the rail. The design puts this above the
 * block list, so it does not go through the Customize form.
 *
 * Fourteen swatches would take the rail's whole height, and the blocks below
 * are what gets used. The rail shows the three this account reaches for most,
 * and More opens the rest in place. In place rather than in a modal: the
 * preview sits alongside, and a dialog would cover the very thing you are
 * choosing between. Picking a theme leaves the list open so several can be
 * tried in turn.
 */
const SMLThemePickerComponent: React.FC<SMLThemePickerProps> = ({ smartLink, isExpanded, onExpandedChange }) => {
  const intl = useIntl();
  const [updateSmartLink, { isLoading }] = useUpdateSmartLinkMutation();
  const saves = useEditorSaves();
  const { name, slug, theme, backgroundFile } = smartLink.recentSmartLinkVersion;
  const featured = useFeaturedThemes(smartLink.accountId, theme);

  const expand = useCallback(() => onExpandedChange(true), [onExpandedChange]);
  const collapse = useCallback(() => onExpandedChange(false), [onExpandedChange]);

  const onThemeClick = useCallback(
    (value: string) => {
      if (value === theme) {
        return;
      }
      // name, slug and backgroundFileId must ride along: the PATCH replaces
      // them, and omitting backgroundFileId clears the background image.
      const call = updateSmartLink({
        accountId: smartLink.accountId,
        smartLinkId: smartLink.id,
        name,
        slug,
        backgroundFileId: backgroundFile?.id,
        theme: value as SerializedSmartLinkVersionTheme,
      }).unwrap();
      // In the editor, Publish waits for the theme to save.
      (saves?.track(call) ?? call).then(() => SMLAnalytics.trackSmartLinkCustomizeChanged()).catch(console.error);
    },
    [updateSmartLink, saves, smartLink.accountId, smartLink.id, name, slug, backgroundFile?.id, theme]
  );

  const label = intl.formatMessage({ id: 'app.sml.smartlinks.creator.theme', defaultMessage: 'Theme' });

  if (isExpanded) {
    return (
      <SMLRailPanel
        grow
        label={intl.formatMessage({ id: 'app.sml.smartlinks.creator.theme.all', defaultMessage: 'All themes' })}
        right={
          <button
            type="button"
            className={styles.close}
            onClick={collapse}
            aria-label={intl.formatMessage({ id: 'app.sml.smartlinks.creator.theme.close', defaultMessage: 'Close' })}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        }
      >
        <div className={styles.all}>
          {allThemes.map((option) => (
            <SMLThemeSwatch
              key={option}
              option={option}
              checked={theme === option}
              onClick={onThemeClick}
              disabled={isLoading}
            />
          ))}
        </div>
      </SMLRailPanel>
    );
  }

  return (
    <SMLRailPanel label={label}>
      <div className={styles.themes}>
        {featured.map((option) => (
          <SMLThemeSwatch
            key={option}
            option={option}
            checked={theme === option}
            onClick={onThemeClick}
            disabled={isLoading}
          />
        ))}
        <button type="button" className={styles.more} onClick={expand} disabled={isLoading}>
          {intl.formatMessage(
            { id: 'app.sml.smartlinks.editor.look.all_themes', defaultMessage: 'All {count} themes' },
            { count: allThemes.length }
          )}
        </button>
      </div>
    </SMLRailPanel>
  );
};

export const SMLThemePicker = TypedMemo(SMLThemePickerComponent);
